import { useEffect, useRef, useState } from "react";
import { Mic, Square, LoaderCircle, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useRecorder } from "@/hooks/useRecorder";
import { SpeakButton } from "@/components/SpeakButton";

const scoreTone = (s) => (s >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : s >= 60 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-rose-700 bg-rose-50 border-rose-200");

export const PronunciationPractice = ({ text, romaji, lang, onResult, testPrefix = "pron" }) => {
  const { recording, start, stop } = useRecorder();
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const finish = async () => {
    clearTimeout(timer.current);
    const blob = await stop();
    if (!blob) return;
    setState("scoring");
    const form = new FormData();
    const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
    form.append("file", blob, `speech.${ext}`);
    form.append("language", lang);
    form.append("expected", text);
    try {
      const { data } = await api.post("/speech/evaluate", form);
      setResult(data);
      setState("done");
      onResult?.(data);
    } catch (e) {
      setError(errMsg(e));
      setState("idle");
    }
  };

  const begin = async () => {
    setError("");
    setResult(null);
    try {
      await start();
      setState("recording");
      timer.current = setTimeout(finish, 8000);
    } catch {
      setError("Microphone access is needed. Please allow it in your browser.");
    }
  };

  return (
    <div className="flex flex-col items-center text-center gap-4" data-testid={`${testPrefix}-practice`}>
      <div className="flex items-center gap-3">
        <div>
          <p className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{text}</p>
          {romaji && <p className="text-sm text-[hsl(var(--accent-blue))] font-semibold">{romaji}</p>}
        </div>
        <SpeakButton text={text} lang={lang} testId={`${testPrefix}-listen`} />
      </div>
      {state !== "done" && (
        <button
          type="button"
          data-testid={`${testPrefix}-record-button`}
          disabled={state === "scoring"}
          onClick={recording ? finish : begin}
          className={`h-20 w-20 rounded-full grid place-items-center text-white transition-transform active:scale-95 ${recording ? "bg-rose-500 animate-pulse" : "bg-[hsl(var(--secondary))] hover:scale-105"} shadow-lg`}
          aria-label={recording ? "Stop recording" : "Start recording"}
        >
          {state === "scoring" ? <LoaderCircle size={30} className="animate-spin" /> : recording ? <Square size={26} className="fill-white" /> : <Mic size={32} />}
        </button>
      )}
      <p className="text-sm text-slate-500">
        {state === "recording" ? "Listening... tap to stop" : state === "scoring" ? "Checking your pronunciation..." : state === "idle" ? "Tap the mic and say the phrase" : ""}
      </p>
      {error && <p data-testid={`${testPrefix}-error`} className="text-sm text-rose-600 font-medium">{error}</p>}
      {result && (
        <div data-testid={`${testPrefix}-result`} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left animate-fade-up">
          <div className="flex items-center gap-3">
            <span data-testid={`${testPrefix}-score`} className={`font-display text-2xl font-extrabold px-3 py-1 rounded-xl border ${scoreTone(result.score)}`}>{result.score}</span>
            <p className="text-sm text-slate-700 flex-1">{result.feedback}</p>
          </div>
          <p className="text-sm text-slate-500 mt-3">We heard: <b className="text-slate-800">{result.heard || "(nothing)"}</b></p>
          {result.tip && <p className="text-sm text-slate-700 mt-1"><b>Tip:</b> {result.tip}</p>}
          <button type="button" data-testid={`${testPrefix}-retry-button`} onClick={begin} className="mt-3 text-sm font-bold text-[hsl(var(--primary-dark))] inline-flex items-center gap-1">
            <RotateCcw size={14} /> Try again
          </button>
        </div>
      )}
    </div>
  );
};
